import { CaretDownOutlined } from '@ant-design/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { MailchimpIcon } from '@mm/icons';
import { useMailchimpConnectionCRUDActions } from '@mm/state/actions/MailchimpConnectionsCRUDActions';
import { useMailchimpCRUDActions } from '@mm/state/actions/MailchimpCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import {
  ConnectionKind,
  FormattedConnectionModel,
} from '@mm/state/model/ConnectionModel';
import { MailchimpConnections } from '@mm/state/model/MailchimpModel';
import { useConnectionsTableActions } from '@mm/state/modules/connectionsTableState/actions';
import { selectMailchimpAccounts } from '@mm/state/selectors/mailchimpSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { Collapse, CollapseProps, ConfigProvider, Table } from 'antd';
import { ColumnType } from 'antd/es/table/interface';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Menu } from '../../../../Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { IntegrationErrors } from '../../../constants';
import { ReactComponent as AddMailchimpIcon } from '../../../icons/add-mailchimp.svg';
import '../../IntegrationBaseComponent.scss';
import styles from './MailchimpIntegration.module.css';
import { MailchimpHeaderComponent } from './MailchimpHeaderComponent';
import { Button } from '@mm/components';
import { useGuardedConnectOAuthToken } from '@mm/stateful-components/OAuth/use-guarded-connect-o-auth-token';

type Props = {
  isAccountConnected: boolean;
  onDisconnect: (id: number) => void;
  mailchimpCount: number;
  columns: ColumnType<FormattedConnectionModel>[];
  mailchimpConnections: MailchimpConnections[];
  name?: string;
};

export const IntegrationMailchimpComponent = (props: Props) => {
  const {
    isAccountConnected,
    onDisconnect,
    mailchimpCount,
    columns,
    mailchimpConnections,
    name,
  } = props;

  const accounts = useSelector(selectMailchimpAccounts);
  const { getMailchimpOauthUrl } = useMailchimpCRUDActions();
  const { create } = useMailchimpConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const { setActiveIntegration } = useConnectionsTableActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const [haveErrors, setHaveErrors] = useState(false);

  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const getLink = useCallback(async () => {
    const link = await getMailchimpOauthUrl();
    return link;
  }, [getMailchimpOauthUrl]);

  useEffect(() => {
    const errorsString = mailchimpConnections
      .map(connection =>
        connection.connections.map(webhook =>
          webhook.webhookErrors?.map(error => error.errorDetails),
        ),
      )
      .flat()
      .join('');
    setHaveErrors(
      IntegrationErrors.some(error =>
        errorsString.toLowerCase().includes(error),
      ),
    );
  }, [mailchimpConnections]);

  const sectionProps = {
    landmineName: landmineNames?.INTEGRATIONS_MAILCHIMP,
    featureFlag: 'nativeIntegrations',
  };

  const renderTable = (dataSource: any[]) => (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: 'white',
            borderColor: '#D9D9D9',
            cellPaddingBlockSM: 3,
          },
        },
      }}
    >
      <div className="integration-panel--ant-table">
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          size="middle"
          bordered
        />
      </div>
    </ConfigProvider>
  );

  const items: CollapseProps['items'] = mailchimpConnections
    .filter(c => c.connections.length || c.accountEmail)
    .map(c => ({
      key: c.accountId?.toString(),
      label: (
        <span className="integrations-grid___account-name">
          {c.accountEmail ? c.accountEmail : 'Not Assigned'}{' '}
        </span>
      ),
      children: renderTable(c.connections),
      extra: (
        <Menu
          className={styles.menu}
          menu={[
            {
              children: (
                <div
                  className={classNames(styles.menuAction, {
                    [styles.menuActionDisabled]: accounts.find(
                      acc => acc.id === c.accountId && acc.readOnly,
                    ),
                  })}
                >
                  Disconnect
                </div>
              ),
              onClick: () => {
                if (
                  accounts.find(acc => acc.id === c.accountId && !acc.readOnly)
                ) {
                  onDisconnect(
                    Number(accounts.find(acc => acc.id === c.accountId)?.id),
                  );
                }
              },
            },
          ]}
          direction="bottom"
          useThreeDotButton
        />
      ),
    }));

  const addNewConnection = async () => {
    const connection = await create(activeFbPageId, {
      name: 'Mailchimp Connection',
      description: '',
      kind: ConnectionKind.AUDIENCE,
      connectionKind: 'mailchimp',
    });
    if (connection) {
      showSlideoutMenu('mailchimpIntegrationEditor', {
        widgetId: connection.id,
      });
    }
  };

  const onClickAddNew = () => {
    addNewConnection();
    setActiveIntegration(name);
  };

  const { connect: connectOauth } = useGuardedConnectOAuthToken(
    'nativeIntegrations',
    landmineNames.INTEGRATIONS_MAILCHIMP,
    false,
    'mailchimpConnect',
    'mailchimpTokenReceived',
    getLink,
    onClickAddNew,
    { width: 600, height: 600 },
    '_self',
  );

  const headerComponent = (
    <MailchimpHeaderComponent
      isAccountConnected={isAccountConnected}
      mailchimpCount={mailchimpCount}
      accountsCount={accounts.length}
      onConnect={connectOauth}
    />
  );

  return (
    <BaseIntegration
      openOnMount
      titleComponent={
        <BaseIntegrationHeader
          title="Mailchimp"
          subtitle={`Automatically send data from ${companyName} to Mailchimp`}
          icon={<MailchimpIcon />}
          headerRightComponent={headerComponent}
          css={{ icon: 'integrations__mailchimp-header-icon' }}
          haveErrors={haveErrors}
          {...sectionProps}
        />
      }
      integrationType="mailchimp"
      showEmptyState={!isAccountConnected || !mailchimpConnections.length}
      emptyStateConfig={{
        button: !isAccountConnected
          ? 'Add a Mailchimp Account'
          : 'Add a Connection',
        onClick: !isAccountConnected ? connectOauth : onClickAddNew,
        icon: <AddMailchimpIcon />,
        ...sectionProps,
      }}
    >
      <div className={styles.container} style={{ marginTop: 16 }}>
        <div className={styles.label}>Destinations</div>
        <div className={styles.content}>
          <div className={styles.header}>
            <div className={styles.headerTitle}>
              CRM connections
              <div className={styles.headerSubtitle}>
                Send leads directly to your CRM
              </div>
            </div>
            <Button
              css={{ container: 'integrations__header-add-button-secondary' }}
              onClick={e => {
                e?.stopPropagation();
                onClickAddNew();
              }}
              color="transparent-no-shadow"
              disabled={!accounts.find(acc => !acc.readOnly)}
            >
              Add a Connection
            </Button>
          </div>
        </div>
        <Collapse
          items={items}
          className="integrations__collapse"
          expandIcon={({ isActive }) => (
            <CaretDownOutlined
              style={{ fontSize: 20 }}
              rotate={isActive ? 180 : 0}
            />
          )}
        />
      </div>
    </BaseIntegration>
  );
};
